/**
 * GmEventScoreboard — live scoreboard window for the active GM event.
 *
 * Subscribes to `gmEventScoreboard` broadcasts via useGmEvent.
 * Shows participant rankings sorted by score.
 *
 * @see docs/design/GM_EVENT_INTEGRATION_SPEC.md §3.17
 */

import React from 'react';
import { useGmEvent } from '../../../services/GmEventService';
import { COLORS, FONTS } from '../../theme';

export const GmEventScoreboardContent: React.FC = () => {
  const { scoreboard, announcement } = useGmEvent();

  if (!scoreboard && !announcement) {
    return (
      <div style={{ padding: 16, color: COLORS.textMuted, fontFamily: FONTS.ui }}>
        No active event. Join one with /eventjoin or create one with /eventcreate.
      </div>
    );
  }

  const entries = scoreboard?.entries ?? [];
  const sorted = [...entries].sort((a, b) => b.score - a.score);

  return (
    <div style={{ padding: 16, color: COLORS.textPrimary, fontFamily: FONTS.ui }}>
      {announcement && (
        <h3 style={{ margin: '0 0 12px', color: COLORS.textAccent }}>
          🏆 {announcement.title} — Scoreboard
        </h3>
      )}
      {sorted.length === 0 ? (
        <p style={{ color: COLORS.textMuted }}>No participants yet.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${COLORS.border}`, textAlign: 'left' }}>
              <th style={{ padding: '4px 8px' }}>#</th>
              <th style={{ padding: '4px 8px' }}>Player</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Score</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Kills</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Deaths</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((entry, i) => {
              const localSid: string | null = (window as any).nm?.getRoom?.()?.sessionId ?? null;
              const isMe = entry.sessionId === localSid;
              return (
                <tr
                  key={entry.sessionId}
                  style={{
                    borderBottom: `1px solid ${COLORS.borderFaint}`,
                    background: isMe ? COLORS.surfaceHover : 'transparent',
                  }}
                >
                  <td style={{ padding: '4px 8px', color: COLORS.textMuted }}>{i + 1}</td>
                  <td style={{ padding: '4px 8px', fontWeight: isMe ? 'bold' : 'normal' }}>
                    {entry.heroName}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: COLORS.textAccent, fontWeight: 'bold' }}>
                    {entry.score}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{entry.kills}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{entry.deaths}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
